import * as Yup from 'yup';
import {
  APLHABET_PATTERN_REGEX,
  CANDIDATE_SOURCE_TYPE,
  InterviewMode,
  InterviewType,
  MOBILE_NUMBER_REGEX,
  TEXTAREA_LIMIT
} from '../constants/common';
import { resumeValidation } from '../utils/common';

export const addEditCandidateJobSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .min(2, 'Must be at least 2 characters')
    .max(15, 'Must be 15 characters or less')
    .matches(APLHABET_PATTERN_REGEX, 'First Name should only contain characters')
    .required('First Name is required.'),
  lastName: Yup.string()
    .trim()
    .min(2, 'Must be at least 2 characters')
    .max(20, 'Must be 20 characters or less')
    .matches(APLHABET_PATTERN_REGEX, 'Last Name should only contain characters')
    .required('Last Name is required.'),
  mobile: Yup.string()
    .trim()
    .matches(MOBILE_NUMBER_REGEX, 'Mobile Number Must Be 10 Digits Only.')
    .required('Mobile Number is required.'),
  secondaryMobile: Yup.string()
    .trim()
    .matches(MOBILE_NUMBER_REGEX, 'Mobile Number Must Be 10 Digits Only.')
    .when('mobile', {
      is: (mobile) => mobile && mobile.length === 10,
      then: Yup.string().notOneOf(
        [Yup.ref('mobile')],
        'Secondary Mobile Number is same as the Mobile Number.'
      )
    }),
  gender: Yup.string(['1', '2']).required('Gender is required.'),
  isEdit: Yup.boolean(),
  isScheduleInterviewEnabled: Yup.boolean(),
  yearOfExperience: Yup.number()
    .min(0, 'Minimum 0 Year.')
    .max(100, 'Maximum 100 Years.')
    .typeError('Only numbers are allowed.')
    .required('Exp. in Years is Required.'),
  monthsOfExperience: Yup.number()
    .min(0, 'Minimum 0 Month.')
    .max(11, 'Maximum 11 Months.')
    .required('Exp. in Months is required.')
    .typeError('Only numbers are allowed.'),
  interviewTechnology: Yup.array()
    .required('Technology is required.')
    .min(1, 'Minimum 1 technology is required.'),

  email: Yup.string().when('isEdit', {
    is: true,
    then: Yup.string().email('Enter a valid email address').required('Email Address is required.')
  }),
  interviewType: Yup.string().when(['isEdit', 'isScheduleInterviewEnabled'], {
    is: true,
    then: Yup.string().required('Interview Type is required.')
  }),
  interviewRoundMode: Yup.string().when(['isEdit', 'isScheduleInterviewEnabled'], {
    is: true,
    then: Yup.string().typeError('Please select interview round mode')
  }),
  duration: Yup.number().when(['isEdit', 'isScheduleInterviewEnabled'], {
    is: true,
    then: Yup.number().typeError('Interview duration is required.')
  }),
  meetingLink: Yup.string()
    .trim()
    .when('interviewRoundMode', {
      is: (mode) => {
        return Number(mode) === InterviewMode.Online;
      },
      then: Yup.string()
        .required('Meeting link is required for online interview')
        .url('Please enter a valid meeting link'),
      otherwise: Yup.string().nullable()
    }),
  interviewerLimit: Yup.number().when(['isEdit', 'isScheduleInterviewEnabled'], {
    is: (isEdit, isScheduleInterviewEnabled) => isEdit && isScheduleInterviewEnabled,
    then: Yup.number()
      .min(1, 'Minimum Limit 1')
      .max(10, 'Maximum  Limit 10')
      .typeError('Only numbers are allowed!')
      .required('Interviewer Limit is required.')
  }),
  // interviewerID: Yup.array().when(['isEdit', 'isScheduleInterviewEnabled'], {
  //   is: (isEdit, isScheduleInterviewEnabled) => isEdit && isScheduleInterviewEnabled,
  //   then: Yup.array().required('Interviewer is required.').min(1, 'Interviewer is required.')
  // }),
  interviewerID: Yup.array().when(['isEdit', 'isScheduleInterviewEnabled', 'interviewerLimit'], {
    is: (isEdit, isScheduleInterviewEnabled, interviewerLimit) =>
      isEdit && isScheduleInterviewEnabled && !!interviewerLimit,
    then: Yup.array()
      .test(
        'exact-interviewer-limit',
        'Number of selected interviewers must exactly match the interviewer limit.',
        function (value) {
          const { interviewerLimit } = this.parent;
          if (!Array.isArray(value)) return false;
          return value.length === Number(interviewerLimit?.code || interviewerLimit);
        }
      )
      .required('Interviewer is required.'),
    otherwise: Yup.array().notRequired()
  }),
  feedbackParameterIds: Yup.array().when(
    ['isEdit', 'isScheduleInterviewEnabled', 'interviewType'],
    {
      is: (isEdit, isScheduleInterviewEnabled, interviewType) =>
        isEdit &&
        isScheduleInterviewEnabled &&
        (interviewType === InterviewType['Technical Interview - 1'].toString() ||
          interviewType === InterviewType['Technical Interview - 2'].toString() ||
          interviewType === InterviewType['Management Round'].toString()),
      then: Yup.array()
        .required('Feedback Parameter is required.')
        .min(1, 'Feedback Parameter is required.')
    }
  ),
  interviewCategory: Yup.string(),
  jobDesignation: Yup.string().when(['isEdit', 'isScheduleInterviewEnabled'], {
    is: true,
    then: Yup.string().nullable()
  }),
  description: Yup.string()
    .trim()
    .max(TEXTAREA_LIMIT.MAX, 'Description should not exceed ${max} characters.')
    .nullable(),
  resumeFilePath: Yup.mixed().test(
    'format',
    'Only the following formats are accepted: .doc, .docx, .pdf',
    (value) => {
      if (!value) {
        return true;
      }
      return resumeValidation(value);
    }
  ),
  // candidatePhotoPath: Yup.mixed()
  //   .test('format', 'Only the following formats are accepted: .png, .jpg, .jpeg', (value) => {
  //     if (!value) {
  //       return true;
  //     }
  //     return imageValidation(value);
  //   })
  //   .test('fileSize', 'The image size must be less than 1 MB', (value) => {
  //     if (!value) {
  //       return true;
  //     }
  //     return value.size <= 1024 * 1024; // Check if file size is less than 1 MB.
  //   }),
  candidateLocation: Yup.string('Candidate location must be of type string.')
    .trim()
    .required('Candidate location is required.'),
  location: Yup.string('Candidate Location must be of type string.')
    .trim()
    .when('candidateLocation', {
      is: 'other',
      then: Yup.string().required('Candidate Location is required.'),
      otherwise: Yup.string()
    }),
  currentCTC: Yup.number('Current CTC must be of type number.')
    .min(0, 'CTC should not be less then 0.')
    .max(99, 'CTC should not be more then 99.')
    .typeError('Only positive numbers are allowed.')
    .required('Current CTC is required.'),
  currentCompany: Yup.string('Current company must be of type string.')
    .trim()
    .required('Current Company is required.'),
  candidateSource: Yup.number()
    .required('Candidate source is required.')
    .oneOf(
      CANDIDATE_SOURCE_TYPE.map((candidateSource) => candidateSource.code),
      'Valid candidate source is required.'
    ),
  referenceEmployeeName: Yup.string('Candidate reference must be of type string.').trim(),
  callStatus: Yup.number().required('Status of call is required.'),
  screeningCall: Yup.string()
    .trim()
    .max(5000, 'Screening call note should not exceed 5000 characters.')
    .nullable()
});
